import { useRef, useState, type FocusEvent } from "react";
import { FaPaste } from "react-icons/fa";

import { openLink } from "@tma.js/sdk-react";

import { Description } from "pages/ui/page";
import { Input } from "shared/ui/input";
import { cn } from "shared/utils/cn";

const APP_SPECIFIC_PASSWORD_REGEX = /^[a-z]{16}$/;

export const normalizeAppPassword = (raw: string) =>
  raw.replace(/[\s-]/g, "").toLowerCase();

export const isAppPasswordValid = (raw: string) =>
  Boolean(raw) && APP_SPECIFIC_PASSWORD_REGEX.test(normalizeAppPassword(raw));

const SAVED_PASSWORD_MASK = "****************";

type Props = {
  className?: string;
  description: string;
  learnMoreUrl: string;
  value: string;
  error?: string;
  // Renders the input as read-only and "filled" until the user taps it to
  // enter a new password (edit page for accounts with a stored password).
  hasSavedPassword?: boolean;
  onChange: (value: string) => void;
};

export const AppPasswordSection = (props: Props) => {
  const {
    className,
    description,
    learnMoreUrl,
    value,
    error,
    hasSavedPassword,
    onChange,
  } = props;

  const [isEditing, setIsEditing] = useState(!hasSavedPassword);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const savedScrollRef = useRef(0);

  const handleFocus = () => {
    if (!isEditing) setIsEditing(true);
    if (!isKeyboardOpen) {
      savedScrollRef.current = window.scrollY;
      // The spacer below the section guarantees there is always enough room
      // to scroll the section header to the very top of the viewport.
      setIsKeyboardOpen(true);
    }
    // Wait for the webview to finish resizing for the keyboard, then pin the
    // section header to the top so previous steps scroll out of view.
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  const handleBlur = (e: FocusEvent) => {
    // Focus moved inside the section (e.g. the Paste button) — keep the state.
    if (sectionRef.current?.contains(e.relatedTarget as Node)) return;
    setIsKeyboardOpen(false);
    setTimeout(() => {
      window.scrollTo({ top: savedScrollRef.current, behavior: "smooth" });
    }, 250);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      onChange(text);
    } catch (pasteError) {
      console.error(pasteError);
    }
  };

  return (
    <section ref={sectionRef} className={cn("flex flex-col gap-2", className)}>
      <Description>Authorization</Description>

      <p className="text-sm leading-5 tracking-[0.4px] text-white/40">
        {description}{" "}
        <button
          type="button"
          onClick={() => openLink(learnMoreUrl, { tryInstantView: false })}
          className="text-clear-blue underline"
        >
          Learn how to generate one here
        </button>
      </p>

      <Input
        type="password"
        enterKeyHint="done"
        value={isEditing ? value : SAVED_PASSWORD_MASK}
        readOnly={!isEditing}
        placeholder="XXXX-XXXX-XXXX-XXXX"
        error={error}
        className="mb-3"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        onChange={(e) => onChange(e.target.value)}
      >
        {isEditing && (
          <button
            type="button"
            onClick={handlePaste}
            className="text-clear-blue flex items-center gap-x-1.5 text-sm leading-5.5 font-medium tracking-[0.4px]"
          >
            Paste
            <FaPaste className="size-5" />
          </button>
        )}
      </Input>

      <div className="border-pale-red text-pale-red bg-pale-red/8 rounded-xl border px-3 py-2.5 text-center text-sm leading-5 font-medium tracking-[0.4px] uppercase">
        This is not your account password!
      </div>

      {isKeyboardOpen && <div aria-hidden className="h-dvh shrink-0" />}
    </section>
  );
};
